import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  apiPath,
  firstListItem,
  TimePeriodType,
} from '@/constants/defaultValues';
import { Delete, Get, Post, Put } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';

const initialState = {
  policyDynamicFormList: [],
  policyOvertimeDynamicFormList: [],
  timePeriodsAll: [],
  policyAddUpdateDelete: {},
  policyAssignmentAddUpdateDelete: {},
  timePeriodItem: {},
  policyAssignedItemsList: [],
  notification: { show: false, code: '' },
  loading: false,
  error: '',
};

export const fetchPolicyDynamicForm = createAsyncThunk(
  'policy/fetchPolicyDynamicForm',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/System/GetDynamicFormSpec/${params}/${getCompanyID()}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateTimePeriod = createAsyncThunk(
  'timePeriod/updateTimePeriod',
  async (params, thunkAPI) => {
    try {
      const response = await Put({
        url: `${apiPath}${'api/Lookup/TimePeriods/Update/'}${params.id}`,
        data: params.request,
      });
      // thunkAPI.dispatch(getAllTimePeriods(params?.type));
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getAllTimePeriods = createAsyncThunk(
  'timePeriod/getAllTimePeriods',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/TimePeriods/GetAll/${getCompanyID()}/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const filterTimePeriods = createAsyncThunk(
  'timePeriod/filterTimePeriods',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/TimePeriods/Filter'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addUpdateTimePeriod = createAsyncThunk(
  'timePeriod/addUpdateTimePeriod',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/TimePeriods/Add'}`,
        data: params,
      });
      if (response?.data?.errCode == '555') {
        return response?.data?.data;
      }
      // thunkAPI.dispatch(getAllTimePeriods(params.type));
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addPolicyAssignment = createAsyncThunk(
  'policy/addPolicyAssignment',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/PolicyAssignment/Add'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteRamadanPeriod = createAsyncThunk(
  'timePeriod/deleteRamadanPeriod',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/TimePeriods/Delete/${params?.id}`,
      });
      thunkAPI.dispatch(getAllTimePeriods(params?.type));
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const timePeriodSlice = createSlice({
  name: 'timePeriod',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.policyDynamicFormList = [];
      state.policyOvertimeDynamicFormList = [];
      state.timePeriodsAll = [];
      state.policyAddUpdateDelete = {};
      state.policyAssignmentAddUpdateDelete = {};
      state.timePeriodItem = {};
      state.policyAssignedItemsList = [];
      state.notification = { show: false, code: '' };
      state.loading = false;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicyDynamicForm.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchPolicyDynamicForm.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.find((x) => x.formCode === 'TAPolicy')) {
          state.policyDynamicFormList = action.payload;
        } else if (
          action.payload?.find((x) => x.formCode === 'TAPolicyOverTime')
        ) {
          state.policyOvertimeDynamicFormList = action.payload;
        }
        state.error = '';
      })
      .addCase(fetchPolicyDynamicForm.rejected, (state, action) => {
        state.loading = false;
        state.policyDynamicFormList = [];
        state.policyOvertimeDynamicFormList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(updateTimePeriod.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(updateTimePeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.timePeriodItem = action.payload?.data;
        state.notification = { show: true, code: action.payload?.errCode };
        state.error = '';
      })
      .addCase(updateTimePeriod.rejected, (state, action) => {
        state.loading = false;
        state.timePeriodItem = {};
        state.error = action.payload?.data ?? 'An error occurred';
      });

    builder
      .addCase(getAllTimePeriods.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllTimePeriods.fulfilled, (state, action) => {
        state.loading = false;
        state.timePeriodsAll = action.payload;
        state.error = '';
      })
      .addCase(getAllTimePeriods.rejected, (state, action) => {
        state.loading = false;
        state.timePeriodsAll = [];
        state.error = action.payload ?? 'An error occurred';
      });
      builder
      .addCase(filterTimePeriods.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(filterTimePeriods.fulfilled, (state, action) => {
        state.loading = false;
        state.timePeriodsAll = action.payload;
        state.error = '';
      })
      .addCase(filterTimePeriods.rejected, (state, action) => {
        state.loading = false;
        state.timePeriodsAll = [];
        state.error = action.payload ?? 'An error occurred';
      });  

    builder
      .addCase(addUpdateTimePeriod.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addUpdateTimePeriod.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.timePeriodItem = action.payload;
          if (action.payload !== '555') {
            state.notification = { show: true, code: '200' };
            state.error = '';
          }else{
            state.notification = { show: true, code: '555' };
          }
        }
      })
      .addCase(addUpdateTimePeriod.rejected, (state, action) => {
        state.loading = false;
        state.timePeriodItem = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addPolicyAssignment.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addPolicyAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(addPolicyAssignment.rejected, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(deleteRamadanPeriod.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteRamadanPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.timePeriodItem = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteRamadanPeriod.rejected, (state, action) => {
        state.loading = false;
        state.timePeriodItem = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem } = timePeriodSlice.actions;
export default timePeriodSlice.reducer;
